import CommentsSheet from "@/components/feed/CommentsSheet";
import RichTextView from "@/components/common/RichTextView";
import DMPaywallModal from "@/components/messaging/DMPaywallModal";
import PostUnlockModal from "@/components/feed/PostUnlockModal";
import AVSBlurBadge from "@/components/media/AVSBlurBadge";
import { Heart, MessageCircle, MoreHorizontal, Send, X, Flag, EyeOff, VolumeX, BarChart3, BadgeCheck, Lock, Sparkles, Bookmark, Music2, Play, Pause } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { filterCss } from "@/lib/mediaEffects";
import api from "@/lib/api";

export default function FeedCard({ post }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post._user_liked || false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  // iter150 · Poll-State
  const [poll, setPoll] = useState(post.poll || null);
  const [pollBusy, setPollBusy] = useState(false);
  const [pollError, setPollError] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  // iter79 Batch 4: Post-Overflow-Menü
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [dmPaywall, setDmPaywall] = useState(false); // iter172c · EU-AI-Act ähnlich: 403 → Paywall
  const [unlockModal, setUnlockModal] = useState(false); // iter230 · Post-Verkaufsübersicht
  // iter148: Impression-Tracker via IntersectionObserver + Dwell-Timer
  const cardRef = useRef(null);
  // Phase 3 · Effekte + Musik Playback
  const fx = post.effects || null;
  const videoFxRef = useRef(null);
  const musicAudioRef = useRef(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const _mediaClean = !post.paywall?.locked && !(post.body_content_present && user?.age_verification_status !== "VERIFIED");
  const fxFilter = (_mediaClean && fx?.filter && fx.filter !== "none") ? filterCss(fx.filter) : undefined;
  useEffect(() => () => { if (musicAudioRef.current) musicAudioRef.current.pause(); }, []);
  const toggleMusic = (e) => {
    e.stopPropagation();
    const url = post.music?.preview_url;
    if (!url) return;
    if (musicPlaying) { musicAudioRef.current?.pause(); setMusicPlaying(false); return; }
    const a = musicAudioRef.current || new Audio(url);
    a.loop = true; a.volume = 0.8;
    a.play().catch(() => {});
    musicAudioRef.current = a;
    setMusicPlaying(true);
  };
  useEffect(() => {
    if (!post?.post_id || !cardRef.current) return undefined;
    let enterTs = null;
    let sent = false;
    const beacon = (dwellMs) => {
      if (sent) return;
      sent = true;
      try {
        const body = JSON.stringify({
          subject_type: "POST", subject_id: post.post_id,
          dwell_ms: dwellMs, source: "feed",
        });
        if (navigator.sendBeacon) {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/view`;
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        } else {
          api.post("/analytics/view", JSON.parse(body)).catch(() => {});
        }
      } catch (e) { /* noop */ }
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          if (!enterTs) enterTs = Date.now();
        } else if (enterTs) {
          const dwell = Date.now() - enterTs;
          if (dwell > 800) beacon(dwell);
          enterTs = null;
        }
      });
    }, { threshold: [0, 0.5, 1.0] });
    io.observe(cardRef.current);
    // Bei unmount: senden falls noch nicht gesendet
    return () => {
      io.disconnect();
      if (enterTs && !sent) beacon(Date.now() - enterTs);
    };
  }, [post?.post_id]);

  const handleReport = async () => {
    setOverflowOpen(false);
    try {
      await api.post("/reports", { target_type: "POST", target_id: post.post_id, reason: "OTHER" });
      window.alert(t("feed.report_success"));
    } catch {
      window.alert(t("feed.report_failed"));
    }
  };
  const handleHide = () => {
    setOverflowOpen(false);
    // Client-side hide: store in localStorage. Bei nächstem Load-Feed filtert Frontend.
    const hidden = JSON.parse(localStorage.getItem("hidden_posts") || "[]");
    if (!hidden.includes(post.post_id)) { hidden.push(post.post_id); localStorage.setItem("hidden_posts", JSON.stringify(hidden)); }
    window.dispatchEvent(new CustomEvent("feed-refresh"));
  };
  const handleMuteAuthor = () => {
    setOverflowOpen(false);
    const muted = JSON.parse(localStorage.getItem("muted_authors") || "[]");
    if (post.author_id && !muted.includes(post.author_id)) { muted.push(post.author_id); localStorage.setItem("muted_authors", JSON.stringify(muted)); }
    window.dispatchEvent(new CustomEvent("feed-refresh"));
    window.alert(t("feed.mute_author_success"));
  };
  const [sendingComment, setSendingComment] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(!!post._user_watched);

  useEffect(() => {
    setLiked(post._user_liked || false);
    setLikeCount(post.like_count || 0);
    setSaved(!!post._user_watched);
  }, [post._user_liked, post.like_count, post._user_watched]);

  const handleLike = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    setError("");
    try {
      // Herz = NUR Like. Speichern erfolgt separat über das 3-Punkte-Menü ("Beitrag speichern").
      const wasLiked = liked;
      const nextLiked = !wasLiked;
      setLiked(nextLiked);
      setLikeCount((c) => nextLiked ? c + 1 : Math.max(0, c - 1));
      if (wasLiked) await api.delete(`/posts/${post.post_id}/like`);
      else await api.post(`/posts/${post.post_id}/like`);
    } catch (err) {
      // Roll back optimistic update
      setLiked(liked);
      setLikeCount(likeCount);
      const msg = err?.response?.data?.detail || t("common.error_generic");
      setError(msg);
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleSavePost = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    setOverflowOpen(false);
    setError("");
    try {
      const { data } = await api.post(`/posts/${post.post_id}/watch`);
      const nowSaved = data.watched !== false;
      setSaved(nowSaved);
      post._user_watched = nowSaved;
      window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
    } catch (err) {
      setError(err?.response?.data?.detail || t("common.error_generic"));
      setTimeout(() => setError(""), 3000);
    }
  };

  const toggleComments = () => {
    // iter79 Batch 4: nutze CommentsSheet Bottom-Sheet statt Inline-Section
    setShowComments(!showComments);
  };

  const sendComment = async () => {
    if (!commentText.trim()) return;
    if (!user || user === false) { navigate("/login"); return; }
    setSendingComment(true);
    setError("");
    try {
      const { data } = await api.post(`/posts/${post.post_id}/comments`, { body: commentText.trim() });
      setComments((prev) => [data, ...prev]);
      setCommentText("");
    } catch (err) {
      const msg = err?.response?.data?.detail || t("feed.comment_failed");
      setError(msg);
      setTimeout(() => setError(""), 3000);
    }
    finally { setSendingComment(false); }
  };

  const handleMessage = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    if (!post.author_id || post.author_id === user.id) return;
    const isStx = post.area_scope === "STILLTOUCHEDX";
    try {
      // iter80: pass post_id → creates/reuses thread per (User × Post)
      // P6 Task2: STX-Posts erzeugen einen STX-Thread (bezahlbar), Social bleibt kostenlos.
      const { data } = await api.post("/messages/social/threads", {
        recipient_id: post.author_id,
        post_id: post.post_id,
        area: isStx ? "STILLTOUCHEDX" : "SOCIAL",
      });
      if (data.first_message_free_consumed) {
        try { sessionStorage.setItem(`first_free_used_${data.thread_id}`, "1"); } catch { /* ignore */ }
      }
      navigate(`/messages/social/${data.thread_id}`);
    } catch (err) {
      // iter172c · DM-Gate-403 → Paywall-Modal öffnen (nur STX)
      if (err?.response?.status === 403 && err?.response?.data?.detail?.code === "DM_GATE_CLOSED") {
        setDmPaywall(true);
        return;
      }
      navigate(`/messages?tab=${isStx ? "stx" : "social"}`);
    }
  };

  // iter160 · openDetail entfernt — Feed rendert Beschreibung + Media direkt inline.
  //          Bestehende Legacy-Links (Bookmarks, DMs) leiten via /posts/:id → / (Feed) weiter.

  const author = post.author || {};
  // iter160 · Titel wird nicht mehr im Feed angezeigt (nur noch im Composer/Studio).
  //          Beschreibung wird bei > 500 Zeichen mit inline „Mehr anzeigen" gekürzt.
  const [descExpanded, setDescExpanded] = useState(false);
  const rawDescHtml = post.description || "";
  const plainDescLen = rawDescHtml.replace(/<[^>]+>/g, "").length;
  const shouldTruncate = plainDescLen > 500;
  const descHtml = (() => {
    if (!shouldTruncate || descExpanded) return rawDescHtml;
    // Kürze anhand des sichtbaren Textes, nicht der HTML-Länge. Wir laufen durch den
    // HTML-String, zählen Text-Zeichen (außerhalb von Tags) und schneiden bei 500 ab —
    // dabei bleiben offene Tags bis dahin erhalten, unschließliche Tags werden am Ende
    // durch einen einfachen Balancer geschlossen.
    let out = "";
    let count = 0;
    let inTag = false;
    const openTags = [];
    for (let i = 0; i < rawDescHtml.length && count < 500; i++) {
      const ch = rawDescHtml[i];
      out += ch;
      if (ch === "<") {
        inTag = true;
        // detect open/close tag name
        const rest = rawDescHtml.slice(i + 1);
        if (rest.startsWith("/")) {
          openTags.pop();
        } else {
          const m = rest.match(/^([a-zA-Z0-9]+)/);
          if (m && !/^(br|img|hr|input|meta|link)$/i.test(m[1])) openTags.push(m[1]);
        }
      } else if (ch === ">") {
        inTag = false;
      } else if (!inTag) {
        count += 1;
      }
    }
    while (openTags.length) out += `</${openTags.pop()}>`;
    return out + "…";
  })();

  return (
    <article ref={cardRef} data-testid={`feed-card-${post.post_id}`} className="flex flex-col bg-white border-b border-[#E5E5E5]">
      {/* Header */}
      <div className="relative flex items-center gap-3 px-4 py-3">
        {(() => {
          const isPlatform = post.author_type === "PLATFORM" || author.is_platform;
          const AvatarInner = author.avatar_url ? (
            <img src={author.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#E5E5E5] flex items-center justify-center text-sm font-bold text-[#999]">
              {(author.display_name || "?")[0]}
            </div>
          );
          if (isPlatform) {
            return (
              <div data-testid={`feed-card-avatar-${post.post_id}`} className="shrink-0 cursor-default" aria-label="stilltouched">
                {AvatarInner}
              </div>
            );
          }
          return (
            <button
              data-testid={`feed-card-avatar-${post.post_id}`}
              onClick={() => author.handle && navigate(post.area_scope === "STILLTOUCHEDX" ? `/user/${author.handle}/stx` : `/user/${author.handle}`)}
              className="shrink-0"
            >
              {AvatarInner}
            </button>
          );
        })()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0A0A0A] truncate flex items-center gap-1.5">
            {author.display_name || t("feed.unknown_author")}
            {(post.author_type === "PLATFORM" || author.is_platform) && (
              <span
                data-testid={`feed-card-official-badge-${post.post_id}`}
                title={t("feed.official_post_title")}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#0A0A0A] text-white uppercase tracking-wider"
              >
                <BadgeCheck size={10} /> Offiziell
              </span>
            )}
          </p>
          {/* iter172b · Badge unter dem Namen (mobile-safe) */}
          {post.show_badge && post.author_type !== "PLATFORM" && !author.is_platform && (
            <button
              data-testid={`feed-card-badge-${post.post_id}`}
              onClick={() => author.handle && navigate(`/user/${author.handle}?tab=exklusiv`)}
              className="mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[#999] text-[9px] font-semibold uppercase tracking-wider hover:bg-[#EDEDED] transition-colors max-w-full truncate"
            >
              {t("feed.exclusive_badge")}
            </button>
          )}
        </div>
        {post.author_type !== "PLATFORM" && !author.is_platform && post.author_id && (!user || user === false || post.author_id !== user.id) && (
          <button data-testid={`feed-card-msg-${post.post_id}`} onClick={handleMessage}
            title={t("feed.send_message")} className="p-1 text-[#999] hover:text-[#0A0A0A] transition-colors">
            <Send size={16} />
          </button>
        )}
        <div className="relative">
          <button data-testid={`feed-card-overflow-${post.post_id}`}
            onClick={() => setOverflowOpen(!overflowOpen)}
            title={t("feed.more_options")}
            className="p-1 text-[#999] hover:text-[#0A0A0A] transition-colors">
            <MoreHorizontal size={18} />
          </button>
          {overflowOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOverflowOpen(false)} />
              <div data-testid={`feed-card-overflow-menu-${post.post_id}`} className="absolute right-0 top-8 z-50 bg-white border border-[#E5E5E5] shadow-lg w-52 py-1">
                <button data-testid={`feed-card-save-${post.post_id}`} onClick={handleSavePost}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#0A0A0A] hover:bg-[#F5F5F5] text-left">
                  <Bookmark size={12} className={saved ? "fill-[#0A0A0A]" : ""} /> {saved ? t("feed.saved_post", { defaultValue: "Gespeichert" }) : t("feed.save_post", { defaultValue: "Beitrag speichern" })}
                </button>
                {post.author_type !== "PLATFORM" && !author.is_platform && (
                  <button data-testid={`feed-card-report-${post.post_id}`} onClick={handleReport}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#0A0A0A] hover:bg-[#F5F5F5] text-left">
                    <Flag size={12} /> {t("feed.report_post")}
                  </button>
                )}
                <button data-testid={`feed-card-hide-${post.post_id}`} onClick={handleHide}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#0A0A0A] hover:bg-[#F5F5F5] text-left">
                  <EyeOff size={12} /> {t("feed.hide_post")}
                </button>
                {post.author_type !== "PLATFORM" && !author.is_platform && (
                  <button data-testid={`feed-card-mute-${post.post_id}`} onClick={handleMuteAuthor}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#0A0A0A] hover:bg-[#F5F5F5] text-left">
                    <VolumeX size={12} /> {t("feed.mute_author")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* iter160 · Titel im Feed entfernt (bleibt im Studio/Composer sichtbar) */}

      {/* iter159 · Beschreibung direkt im Feed rendern (statt Detail-Klick)
          iter160 · Kürzung auf 500 Zeichen mit inline „Mehr anzeigen"-Toggle */}
      {rawDescHtml && (
        <div className="px-4 pb-3">
          <RichTextView
            html={descHtml}
            testId={`feed-card-description-${post.post_id}`}
            className="text-sm leading-relaxed text-[#0A0A0A] prose prose-sm max-w-none"
          />
          {shouldTruncate && (
            <button
              type="button"
              data-testid={`feed-card-desc-toggle-${post.post_id}`}
              onClick={() => setDescExpanded((v) => !v)}
              className="mt-1 text-xs font-semibold text-[#666] hover:text-[#0A0A0A] transition-colors"
            >
              {descExpanded ? t("feed.show_less", "Weniger anzeigen") : t("feed.show_more", "Mehr anzeigen")}
            </button>
          )}
        </div>
      )}

      {/* Media (nicht mehr klickbar) — iter159 · iter160 · Video-Support */}
      {(post.media_url || post.paywall?.locked) && (
        <div data-testid={`feed-card-media-${post.post_id}`}
          className={`block w-full text-left mx-4 mb-1 relative ${post.paywall?.locked ? "cursor-pointer" : ""}`}
          style={{ width: "calc(100% - 2rem)" }}
          onClick={() => {
            // iter230 · Klick aufs geblurrte Bild öffnet den Verkaufs-Modal.
            // Sonst kein Verhalten (Media selbst hat controls / lazy).
            if (post.paywall?.locked) setUnlockModal(true);
          }}>
          <div
            className="w-full overflow-hidden rounded-2xl bg-[#FAFAFA] relative flex items-center justify-center"
            style={{ maxHeight: "60vh", minHeight: "260px" }}
          >
            {post.paywall?.locked && post.is_blur_preview && post.media_url ? (
              // iter234 · Server-Blur-Preview: Bild wird vollflächig mit CSS-Blur
              // gerendert, exakt wie AVS-Fullblur. Server hat bereits ein
              // 48px+Gaussian-geblurrtes JPEG erzeugt → Client zoomt es hoch.
              <img
                src={post.media_url}
                alt=""
                data-testid={`feed-card-blur-preview-${post.post_id}`}
                className="w-full h-auto max-h-[60vh] object-cover blur-2xl scale-125 pointer-events-none select-none"
                loading="lazy"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            ) : !post.media_url && post.paywall?.locked ? (
              // iter233 · Fallback für Alt-Posts ohne Blur-Preview (Videos,
              // gelöschte Assets): farbig-geblurrter Placeholder statt Original.
              (() => {
                const seed = (post.post_id || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
                const hue1 = seed % 360;
                const hue2 = (hue1 + 40 + (seed % 60)) % 360;
                const hue3 = (hue1 + 180) % 360;
                return (
                  <div
                    data-testid={`feed-card-locked-placeholder-${post.post_id}`}
                    className="absolute inset-0 overflow-hidden"
                    style={{
                      background: `radial-gradient(circle at 30% 20%, hsl(${hue1} 55% 45%) 0%, transparent 55%),`
                        + ` radial-gradient(circle at 80% 65%, hsl(${hue2} 60% 42%) 0%, transparent 60%),`
                        + ` radial-gradient(circle at 50% 90%, hsl(${hue3} 40% 30%) 0%, transparent 55%),`
                        + ` linear-gradient(135deg, #1c1c1c, #2d2d2d)`,
                      filter: "blur(2px)",
                    }}
                  >
                    <div
                      className="absolute inset-0 opacity-70 mix-blend-overlay"
                      style={{
                        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='3' stitchTiles='stitch'/></filter><rect width='240' height='240' filter='url(%23n)' opacity='0.9'/></svg>\")",
                      }}
                    />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_5%,rgba(0,0,0,0.35)_75%)]" />
                  </div>
                );
              })()
            ) : (
              <div className="relative inline-block">
              {post.media_type === "VIDEO" ? (
              <video
                ref={videoFxRef}
                data-testid={`feed-card-video-${post.post_id}`}
                src={post.media_url}
                controls={!post.paywall?.locked}
                playsInline
                preload="metadata"
                style={fxFilter ? { filter: fxFilter } : undefined}
                onLoadedMetadata={() => { if (videoFxRef.current && fx?.speed) videoFxRef.current.playbackRate = fx.speed; }}
                className={`block w-auto h-auto max-w-full max-h-[60vh] bg-black ${(post.paywall?.locked || (post.body_content_present && user?.age_verification_status !== "VERIFIED")) ? "blur-2xl scale-110 pointer-events-none" : ""}`}
              />
              ) : (
              <img src={post.media_url} alt=""
                data-testid={`feed-card-image-${post.post_id}`}
                data-body-blurred={!!(post.body_content_present && user?.age_verification_status !== "VERIFIED") || undefined}
                style={fxFilter ? { filter: fxFilter } : undefined}
                className={`block w-auto h-auto max-w-full max-h-[60vh] ${(post.paywall?.locked || (post.body_content_present && user?.age_verification_status !== "VERIFIED")) ? "blur-2xl scale-110" : ""}`}
                loading="lazy" />
              )}
            {/* Phase 3 · Textoverlays (nur wenn Media sichtbar, nicht gesperrt/geblurrt) */}
            {_mediaClean && fx?.texts?.map((tx) => (
              <div key={tx.id || `${tx.x}-${tx.y}-${tx.text}`} data-testid={`feed-card-text-${post.post_id}`}
                className="absolute font-bold leading-tight text-center px-1 pointer-events-none"
                style={{ left: `${(tx.x ?? 0.5) * 100}%`, top: `${(tx.y ?? 0.5) * 100}%`, transform: "translate(-50%,-50%)",
                  color: tx.color || "#FFFFFF", fontSize: `${tx.size || 24}px`, textShadow: "0 1px 3px rgba(0,0,0,0.6)", maxWidth: "92%" }}>
                {tx.text}
              </div>
            ))}
            {/* Phase 3 · Sticker-Overlays (lizenzfreie Emojis) */}
            {_mediaClean && fx?.stickers?.map((st, si) => (
              <div key={st.id || `${si}-${st.x}-${st.y}-${st.emoji}`} data-testid={`feed-card-sticker-${post.post_id}`}
                className="absolute leading-none pointer-events-none select-none"
                style={{ left: `${(st.x ?? 0.5) * 100}%`, top: `${(st.y ?? 0.5) * 100}%`, transform: "translate(-50%,-50%)",
                  fontSize: `${st.size || 56}px`, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}>
                {st.emoji}
              </div>
            ))}
              </div>
            )}
            {/* Phase 3 · Musik-Chip (Play/Pause der Hintergrundmusik) */}
            {_mediaClean && post.music?.preview_url && (
              <button data-testid={`feed-card-music-${post.post_id}`} onClick={toggleMusic}
                className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur text-white rounded-full pl-2 pr-3 py-1.5 max-w-[70%]">
                {musicPlaying ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
                <Music2 size={12} className="shrink-0" />
                <span className="text-[11px] truncate">{post.music.title}</span>
              </button>
            )}
            {/* iter173b · 18+-Badge über AVS-Full-Blur (nicht bei reinem Paywall-Lock) */}
            {post.body_content_present && user?.age_verification_status !== "VERIFIED" && !post.paywall?.locked && (
              <AVSBlurBadge size="md" testId={`feed-card-avs-badge-${post.post_id}`} />
            )}
            {post.paywall?.locked && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 text-white pointer-events-none">
                <Lock size={22} />
                <p className="text-xs uppercase tracking-wider font-bold">
                  {post.paywall.mode === "SINGLE_BUY" && "Einzelkauf"}
                  {post.paywall.mode === "PPV" && "PPV · Abo oder Kauf"}
                  {post.paywall.mode === "SUBSCRIBERS_ONLY" && "Nur für Abonnenten"}
                </p>
                {/* iter261 · Medium-Typ (Video/Bild) sichtbar im Lock-Overlay */}
                <p data-testid={`feed-card-medium-${post.post_id}`} className="text-[10px] uppercase tracking-wider opacity-80 -mt-1">
                  {post.media_type === "VIDEO" ? "Video" : "Bild"}
                </p>
                {post.paywall.price_eur && (
                  <p className="text-2xl font-bold">{Number(post.paywall.price_eur).toFixed(2)} €</p>
                )}
                <p className="text-[10px] uppercase tracking-wider opacity-80 mt-2">
                  Zum Freischalten tippen
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* iter161 · Mentions-Cards: gleiche fixe Höhe wie CreatorSuggestionCard in der Seitenspalte.
          Sidebar-Card hat 3:1 mit Breite ~280px → Höhe ~93px. Wir nutzen h-24 (96px), das Bild
          skaliert per object-cover object-center (vertikal zentriert, oben/unten beschnitten).
          Breite bleibt dynamisch: 1 = full, 2 = half/half, 3 = 2+1, 4 = 2+2, 5 = 2+2+1. Max 5. */}
      {Array.isArray(post.mentioned_authors) && post.mentioned_authors.length > 0 && (() => {
        // iter212 · Nur Mentions mit is_creator=true als CreatorCard rendern.
        const mentions = post.mentioned_authors.filter((m) => m.is_creator === true).slice(0, 5);
        if (mentions.length === 0) return null;
        const rows = [];
        for (let i = 0; i < mentions.length; i += 2) rows.push(mentions.slice(i, i + 2));
        return (
          <div data-testid={`feed-card-mentions-${post.post_id}`} className="mx-4 mb-3 flex flex-col gap-2">
            {rows.map((row, idx) => (
              <div key={idx} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
                {row.map((m) => (
                  <button
                    key={m.handle}
                    type="button"
                    data-testid={`feed-card-mention-card-${m.handle}`}
                    onClick={() => navigate(`/user/${m.handle}`)}
                    className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-[#F5F5F5] hover:border-[#0A0A0A] active:scale-[0.99] transition text-left h-24"
                    aria-label={m.display_name || m.handle}
                  >
                    {(m.creator_card_url || m.banner_url) ? (
                      <img
                        src={m.creator_card_url || m.banner_url}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#333] to-[#666]" />
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                    {/* iter166 · Name rechtsbündig analog Sidebar-CreatorSuggestionCard */}
                    <div className="absolute right-3 bottom-2 flex items-center justify-end max-w-[calc(100%-16px)] pointer-events-none">
                      <p
                        className="text-sm font-black text-white text-right truncate"
                        style={{ textShadow: "0 1px 3px rgba(0,0,0,0.75)" }}
                      >
                        {m.display_name || m.handle}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        );
      })()}

      {/* iter150 · Umfrage-Anzeige */}
      {poll && Array.isArray(poll.options) && poll.options.length > 0 && (() => {
        const totalVotes = poll.total_votes || poll.options.reduce((s, o) => s + (o.votes || 0), 0);
        const now = new Date();
        const expiresAt = poll.expires_at ? new Date(poll.expires_at) : null;
        const expired = expiresAt && now > expiresAt;
        const voted = user && (poll.voters || []).includes(user._id || user.id);
        const showResults = voted || expired;
        const vote = async (optId) => {
          if (pollBusy || !user || user === false) {
            if (!user) navigate("/login");
            return;
          }
          setPollBusy(true); setPollError("");
          try {
            const { data } = await api.post(`/posts/${post.post_id}/poll/vote`, { option_id: optId });
            if (data?.poll) setPoll(data.poll);
          } catch (err) {
            setPollError(err?.response?.data?.detail || "Abstimmung fehlgeschlagen");
          } finally { setPollBusy(false); }
        };
        return (
          <div data-testid={`feed-card-poll-${post.post_id}`} className="mx-4 mb-3 rounded-2xl border border-[#E5E5E5] p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-2 flex items-center gap-1">
              <BarChart3 size={11} /> Umfrage {expired ? "· Beendet" : ""}
            </p>
            <div className="space-y-1.5">
              {poll.options.map((o) => {
                const pct = totalVotes > 0 ? Math.round(((o.votes || 0) / totalVotes) * 100) : 0;
                return (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`feed-card-poll-opt-${post.post_id}-${o.id}`}
                    disabled={showResults || pollBusy}
                    onClick={() => vote(o.id)}
                    className={`relative w-full h-9 px-3 rounded-full border text-sm text-left overflow-hidden ${
                      showResults
                        ? "border-[#E5E5E5] cursor-default"
                        : "border-[#E5E5E5] hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                    }`}
                  >
                    {showResults && (
                      <span
                        className="absolute inset-y-0 left-0 bg-[#F5F5F5]"
                        style={{ width: `${pct}%` }}
                        aria-hidden
                      />
                    )}
                    <span className="relative flex items-center justify-between">
                      <span className="truncate">{o.text}</span>
                      {showResults && (
                        <span className="text-xs font-bold text-[#666] ml-2 shrink-0">{pct} %</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#999] mt-2">
              {totalVotes} {totalVotes === 1 ? "Stimme" : "Stimmen"}
              {expiresAt && !expired && ` · endet ${expiresAt.toLocaleString("de-DE")}`}
            </p>
            {pollError && <p className="text-[10px] text-red-500 mt-1">{pollError}</p>}
          </div>
        );
      })()}

      {/* iter230 · Extra „Für X € freischalten"-Button entfernt (User-Vorgabe).
          Klick aufs geblurrte Bild öffnet stattdessen PostUnlockModal. */}

      {/* iter134 · MP/STX-Attach-Chips (kompakt, klickbar) */}
      {Array.isArray(post.attached_listings) && post.attached_listings.length > 0 && (
        <div className="px-4 pb-2 flex flex-col gap-2">
          {post.attached_listings.map((att) => (
            <button
              key={att.listing_id}
              type="button"
              data-testid={`feed-card-attach-${post.post_id}-${att.listing_id}`}
              onClick={() => {
                // iter166 · Korrekte Routen laut App.js:  /stilltouchedx/:id  bzw.  /marketplace/:id
                const path = att.area_scope === "STILLTOUCHEDX"
                  ? `/stilltouchedx/${att.listing_id}`
                  : `/marketplace/${att.listing_id}`;
                navigate(path);
              }}
              className="flex items-center gap-3 rounded-2xl border border-[#E5E5E5] p-2 hover:bg-[#FAFAFA] text-left"
            >
              <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-[#F5F5F5]">
                {att.thumb_url && <img src={att.thumb_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                {att.is_unique && (
                  <span
                    data-testid={`feed-card-unique-badge-${post.post_id}-${att.listing_id}`}
                    className="inline-flex items-center gap-1 mb-1 px-2 py-0.5 rounded-full bg-[#0A0A0A] text-white text-[9px] font-semibold uppercase tracking-wider"
                    title={t("scarcity.unique_title", { defaultValue: "Unikat — nur einmal verfügbar" })}
                  >
                    <Sparkles size={9} /> {t("feed.unique_badge", { defaultValue: "Unikat" })}
                  </span>
                )}
                <p className="text-[10px] uppercase tracking-wider text-[#999] font-semibold">
                  {att.area_scope === "STILLTOUCHEDX" ? "stilltouchedX" : "Marketplace"}
                </p>
                <p className="text-sm font-medium truncate">{att.title}</p>
              </div>
              <p className="text-sm font-bold shrink-0">{Number(att.price || 0).toFixed(2)} {att.currency || "EUR"}</p>
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && <p className="px-4 py-1 text-xs text-red-500">{error}</p>}

      {/* Actions */}
      <div className="flex items-center gap-5 px-4 py-3">
        <button data-testid={`feed-card-like-${post.post_id}`} onClick={handleLike}
          className="flex items-center gap-1.5 transition-colors active:scale-[0.95]">
          <Heart size={20} className={liked ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-[#666]"} />
          {likeCount > 0 && <span className="text-xs text-[#666]">{likeCount}</span>}
        </button>
        <button data-testid={`feed-card-comment-${post.post_id}`} onClick={toggleComments}
          className="flex items-center gap-1.5 text-[#666] transition-colors active:scale-[0.95]">
          <MessageCircle size={20} className={showComments ? "text-[#0A0A0A]" : ""} />
          {(post.comment_count > 0 || comments.length > 0) && (
            <span className="text-xs">{Math.max(post.comment_count || 0, comments.length)}</span>
          )}
        </button>
        {/* iter154 · Trinkgeld aus Feed-Übersicht entfernt — jetzt nur im Beitragsdetail. */}
      </div>

      {/* iter166 · Alter Badge unter dem Beitrag entfernt — Badge ist jetzt im Header (auf Username-Höhe). */}
      {/* iter79 Batch 4: Comments-Sheet */}
      {showComments && <CommentsSheet postId={post.post_id} onClose={() => setShowComments(false)} />}

      {/* iter172c · DM-Paywall-Modal (bei 403 DM_GATE_CLOSED) */}
      <DMPaywallModal
        open={dmPaywall}
        creatorId={post.author_id}
        creatorName={author.display_name || author.handle}
        onClose={() => setDmPaywall(false)}
      />
      {/* iter230 · Post-Unlock-Modal (Klick auf geblurrtes Bild) */}
      <PostUnlockModal
        open={unlockModal}
        post={post}
        onClose={() => setUnlockModal(false)}
      />
    </article>
  );
}
